import React, { useState, useMemo } from 'react';
import { ProfileAccessLog, Student, AssessmentGrade, SasGrade, FormativeGradeTP, LearningObjective, ActivityType } from '../types';
import { Eye, Search, Clock, Monitor, Globe, User, Trash2, X, Award, PenTool, ClipboardCheck, TrendingUp, BookOpen, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface Props {
  logs: ProfileAccessLog[];
  students: Student[];
  onDelete: (id: string) => void;
  onClearAll?: () => void;
  assessmentGrades?: AssessmentGrade[];
  sasGrades?: SasGrade[];
  formativeGradesTP?: FormativeGradeTP[];
  objectives?: LearningObjective[];
  activeUser?: any;
}

const ProfileAccessHistoryView: React.FC<Props> = React.memo(({ 
  logs, 
  students, 
  onDelete, 
  onClearAll,
  assessmentGrades = [],
  sasGrades = [],
  formativeGradesTP = [],
  objectives = [],
  activeUser
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [logToDelete, setLogToDelete] = useState<string | null>(null);
  const [showClearAllConfirm, setShowClearAllConfirm] = useState(false);
  const [selectedStudentForGrades, setSelectedStudentForGrades] = useState<Student | null>(null);
  const [displayLimit, setDisplayLimit] = useState(100);
  const [selectedSubject, setSelectedSubject] = useState<string>('');

  const { filteredLogs, hasMoreLogs, totalFiltered } = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    const studentMap = new Map(students.map(s => [s.id, s]));

    const result = logs.map(log => ({
      ...log,
      studentData: studentMap.get(log.studentId)
    })).filter(log => {
      const studentName = log.studentData?.name.toLowerCase() || '';
      const accessorName = log.accessorName.toLowerCase();
      const studentClass = (log.studentClass || log.studentData?.class || '').toLowerCase();
      
      if (!query) return true;
      return studentName.includes(query) || accessorName.includes(query) || studentClass.includes(query);
    }).sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    return {
      filteredLogs: result.slice(0, displayLimit),
      hasMoreLogs: result.length > displayLimit,
      totalFiltered: result.length
    };
  }, [logs, students, searchQuery, displayLimit]);

  const uniqueSubjects = useMemo(() => {
    const subs = objectives.map(obj => obj.subject).filter(Boolean);
    return Array.from(new Set(subs)).sort();
  }, [objectives]);

  const currentSubject = selectedSubject || activeUser?.subject || (uniqueSubjects.length > 0 ? uniqueSubjects[0] : '');

  const computedGradesForSubject = useMemo(() => {
    if (!selectedStudentForGrades || !currentSubject) return null;
    const student = selectedStudentForGrades;

    const subjectTPs = objectives.filter(obj => obj.subject === currentSubject);
    const subjectTPIds = new Set(subjectTPs.map(tp => tp.id));

    // Group TP grades
    const tpGradesMap: Record<string, { 
      tpId: string, 
      tpCode: string, 
      tpTitle: string,
      sumatif?: number, 
      praktikum?: number,
      date: string
    }> = {};

    subjectTPs.forEach(tp => {
      tpGradesMap[tp.id] = {
        tpId: tp.id,
        tpCode: tp.code || 'TP',
        tpTitle: tp.description || '',
        date: ''
      };
    });

    // 1. Process standard assessmentGrades
    assessmentGrades.forEach(g => {
      if (g.class !== student.class) return;
      if (g.scores[student.id] === undefined) return;
      if (!subjectTPIds.has(g.tpId)) return;
      
      const tp = tpGradesMap[g.tpId];
      if (!tp) return;

      if (g.activityType === ActivityType.SUMATIF) {
        tpGradesMap[g.tpId].sumatif = g.scores[student.id];
      } else if (g.activityType === ActivityType.PRAKTIKUM) {
        tpGradesMap[g.tpId].praktikum = g.scores[student.id];
      }
      tpGradesMap[g.tpId].date = g.date;
    });

    // 2. Process formativeGradesTP
    if (formativeGradesTP) {
      formativeGradesTP.forEach(fg => {
        if (fg.class !== student.class) return;
        if (!subjectTPIds.has(fg.tpId)) return;
        
        const tp = tpGradesMap[fg.tpId];
        if (!tp) return;

        const sumatifScore = fg.summativeScores?.[student.id];
        if (sumatifScore !== undefined && tpGradesMap[fg.tpId].sumatif === undefined) {
          tpGradesMap[fg.tpId].sumatif = sumatifScore;
        }

        const praktikumScore = fg.praktikumScores?.[student.id];
        if (praktikumScore !== undefined && tpGradesMap[fg.tpId].praktikum === undefined) {
          tpGradesMap[fg.tpId].praktikum = praktikumScore;
        }
      });
    }

    const tpsList = Object.values(tpGradesMap).sort((a, b) => a.tpCode.localeCompare(b.tpCode, undefined, { numeric: true }));

    // Calculate Average Sumatif and Praktikum
    const sumatifScoresForAvg: number[] = [];
    const praktikumScoresForAvg: number[] = [];

    tpsList.forEach(tp => {
      if (tp.sumatif !== undefined) {
        sumatifScoresForAvg.push(tp.sumatif);
      }
      if (tp.praktikum !== undefined) {
        praktikumScoresForAvg.push(tp.praktikum);
      }
    });

    const avgSumatif = sumatifScoresForAvg.length > 0 ? (sumatifScoresForAvg.reduce((a, b) => a + b, 0) / sumatifScoresForAvg.length) : 0;
    const avgPraktikum = praktikumScoresForAvg.length > 0 ? (praktikumScoresForAvg.reduce((a, b) => a + b, 0) / praktikumScoresForAvg.length) : 0;

    // Get raw SAS grade
    const relevantSas = (sasGrades || []).find(g => 
      g.studentId === student.id && 
      g.subject === currentSubject &&
      (g.class === student.class || g.class === 'Semua Kelas')
    );
    let rawSasScore = relevantSas ? relevantSas.score : null;

    // Also look inside assessmentGrades for SAS
    if (rawSasScore === null) {
      assessmentGrades.forEach(g => {
        if ((g.class === student.class || g.class === 'Semua Kelas') && g.activityType === ActivityType.SAS) {
          const score = g.scores[student.id];
          if (score !== undefined && score !== null) {
            rawSasScore = score;
          }
        }
      });
    }

    // Quiz / Tugas Harian
    const classQuizGrades = (assessmentGrades || []).filter(g => 
      (g.class === student.class || g.class === 'Semua Kelas') && 
      ((g.activityType as any) === 'KBM dan Quiz' || 
       (g.activityType as any) === 'Pembelajaran dan Asesmen' || 
       (g.activityType as any) === 'KBM dan Asesmen Harian' || 
       g.activityType === ActivityType.KBM_DAN_QUIZ || 
       g.activityType === ActivityType.KBM)
    );
    const totalQuizAgendas = classQuizGrades.length;
    let sumQuizScores = 0;
    classQuizGrades.forEach(g => {
      const score = g.scores[student.id];
      const num = Number(score);
      if (!isNaN(num) && score !== undefined && score !== null && String(score) !== '') {
        sumQuizScores += num;
      }
    });
    const nilaiTh = totalQuizAgendas > 0 ? Math.round(sumQuizScores / totalQuizAgendas) : 0;

    // Notebook TTD
    let sasNotebookEntries: Record<string, { jumlahTtd: number }> = {};
    let sasNotebookMaxTtd = 0;
    if (activeUser?.id) {
      const keysToTry = [
        `smpn1_notebook_rekap_${activeUser.id}_${student.class}`,
        `smpn1_notebook_rekap_${activeUser.id}_${student.class.replace(/\s+/g, '')}`,
        `smpn1_notebook_rekap_${activeUser.id}_${student.class.replace(/^(\d+)([A-Za-z]+)$/, '$1 $2')}`
      ];
      for (const k of keysToTry) {
        try {
          const saved = localStorage.getItem(k);
          if (saved) {
            const parsed = JSON.parse(saved);
            if (parsed) {
              const entriesObj = parsed.entries || parsed;
              if (entriesObj && typeof entriesObj === 'object' && Object.keys(entriesObj).length > 0) {
                sasNotebookEntries = entriesObj;
                break;
              }
            }
          }
        } catch (e) {}
      }
    }
    const classStudents = (students || []).filter(s => s.class === student.class && s.status === 'Aktif');
    classStudents.forEach(s => {
      const entry = sasNotebookEntries[s.id] || sasNotebookEntries[s.nisn] || sasNotebookEntries[s.nis] || { jumlahTtd: 0 };
      const ttdVal = entry.jumlahTtd || 0;
      if (ttdVal > sasNotebookMaxTtd) sasNotebookMaxTtd = ttdVal;
    });
    const studentEntry = sasNotebookEntries[student.id] || sasNotebookEntries[student.nisn] || sasNotebookEntries[student.nis] || { jumlahTtd: 0 };
    const ttd = studentEntry.jumlahTtd || 0;
    const nilaiCatatan = sasNotebookMaxTtd > 0 ? Math.round((ttd / sasNotebookMaxTtd) * 100) : 0;

    const weightDaily = activeUser?.weightDaily ?? (localStorage.getItem('grading_weight_daily') ? Number(localStorage.getItem('grading_weight_daily')) : 20);
    const weightSas = activeUser?.weightSas ?? (localStorage.getItem('grading_weight_sas') ? Number(localStorage.getItem('grading_weight_sas')) : 80);

    const rawScoreNum = rawSasScore ?? 0;
    let nilaiEvaluasi = Math.round((((nilaiTh + nilaiCatatan) / 2) * (weightDaily / 100)) + (rawScoreNum * (weightSas / 100)));
    if (nilaiEvaluasi < rawScoreNum) {
      nilaiEvaluasi = Math.min(100, rawScoreNum + 1);
    }

    // Weights calculation
    const weights = {
      sumatif: activeUser?.raporSumatifWeight ?? 40,
      praktikum: activeUser?.raporPraktikumWeight ?? 30,
      sas: activeUser?.raporSasWeight ?? 30
    };

    const finalRaw = (avgSumatif * weights.sumatif / 100) + (avgPraktikum * weights.praktikum / 100) + (nilaiEvaluasi * weights.sas / 100);
    const finalScoreFloat = parseFloat(finalRaw.toFixed(2));
    const finalScoreRounded = Math.round(finalRaw);

    return {
      tpsList,
      avgSumatif: parseFloat(avgSumatif.toFixed(2)),
      avgPraktikum: parseFloat(avgPraktikum.toFixed(2)),
      sasScore: nilaiEvaluasi,
      finalScore: finalScoreFloat,
      finalScoreRounded,
      weights
    };
  }, [selectedStudentForGrades, currentSubject, objectives, assessmentGrades, formativeGradesTP, sasGrades, activeUser]);

  return (
    <div className="animate-fade-in space-y-8">
      {logToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl shadow-xl max-w-sm w-full">
            <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-2">Konfirmasi Hapus</h3>
            <p className="text-slate-600 dark:text-slate-400 mb-6">Apakah Anda yakin ingin menghapus log akses ini? Tindakan ini tidak dapat dibatalkan.</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setLogToDelete(null)}
                className="px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
              >
                Batal
              </button>
              <button
                onClick={() => {
                  onDelete(logToDelete);
                  setLogToDelete(null);
                }}
                className="px-4 py-2 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-colors"
              >
                Hapus
              </button>
            </div>
          </div>
        </div>
      )}

      {showClearAllConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl shadow-xl max-w-sm w-full">
            <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-2">Bersihkan Semua Log</h3>
            <p className="text-slate-600 dark:text-slate-400 mb-6">Apakah Anda yakin ingin menghapus SEMUA log akses? Tindakan ini tidak dapat dibatalkan.</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowClearAllConfirm(false)}
                className="px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
              >
                Batal
              </button>
              <button
                onClick={() => {
                  if (onClearAll) onClearAll();
                  setShowClearAllConfirm(false);
                }}
                className="px-4 py-2 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-colors"
              >
                Bersihkan Semua
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Student Grades Popup */}
      <AnimatePresence>
        {selectedStudentForGrades && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedStudentForGrades(null)}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="relative w-full max-w-sm bg-white dark:bg-slate-900 rounded-3xl shadow-xl overflow-hidden animate-fade-in"
            >
              <div className="p-4 sm:p-5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-800/20">
                <div className="flex items-center gap-3 overflow-hidden">
                  <div className="w-10 h-10 shrink-0 bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400 rounded-xl flex items-center justify-center text-lg font-black">
                    {selectedStudentForGrades.name.charAt(0)}
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-black text-slate-800 dark:text-white truncate">
                      {selectedStudentForGrades.name}
                    </h3>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest truncate mt-0.5">
                      Kelas {selectedStudentForGrades.class} • {selectedStudentForGrades.nis}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedStudentForGrades(null)}
                  className="p-1.5 shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-lg transition-all"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-3 sm:p-4 max-h-[60vh] overflow-y-auto">
                {uniqueSubjects.length > 1 && (
                  <div className="mb-4 px-2">
                    <label className="text-[9px] font-black uppercase text-slate-400 tracking-wider block mb-1">Mata Pelajaran</label>
                    <select
                      value={currentSubject}
                      onChange={(e) => setSelectedSubject(e.target.value)}
                      className="w-full text-xs font-bold py-2 px-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-800 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 transition-all text-slate-700 dark:text-slate-300"
                    >
                      {uniqueSubjects.map(sub => (
                        <option key={sub} value={sub}>{sub}</option>
                      ))}
                    </select>
                  </div>
                )}

                {uniqueSubjects.length <= 1 && currentSubject && (
                  <div className="mb-3 px-2 flex justify-between items-center bg-slate-50 dark:bg-slate-800/20 py-1.5 px-2.5 rounded-xl border border-dashed border-slate-200 dark:border-slate-800">
                    <span className="text-[9px] font-black uppercase text-slate-400 tracking-wider">Mata Pelajaran</span>
                    <span className="text-xs font-black text-indigo-600 dark:text-indigo-400">{currentSubject}</span>
                  </div>
                )}

                {computedGradesForSubject ? (
                  <div className="space-y-1">
                    <div className="grid grid-cols-12 gap-2 px-2 py-1 mb-1 text-[8px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100 dark:border-slate-800">
                      <div className="col-span-4">Kode TP</div>
                      <div className="col-span-4 text-center">Sumatif</div>
                      <div className="col-span-4 text-center">Praktik</div>
                    </div>
                    {computedGradesForSubject.tpsList.length > 0 ? (
                      computedGradesForSubject.tpsList.map((tp) => (
                        <div key={tp.tpId} className="grid grid-cols-12 gap-2 p-1.5 hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-lg transition-colors">
                          <div className="col-span-4 flex items-center">
                            <span className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase" title={tp.tpTitle}>
                              {tp.tpCode}
                            </span>
                          </div>
                          <div className="col-span-4 flex items-center justify-center">
                            <span className={`text-xs font-black ${tp.sumatif !== undefined ? 'text-slate-800 dark:text-white' : 'text-slate-300 dark:text-slate-700'}`}>
                              {tp.sumatif ?? '-'}
                            </span>
                          </div>
                          <div className="col-span-4 flex items-center justify-center">
                            <span className={`text-xs font-black ${tp.praktikum !== undefined ? 'text-slate-800 dark:text-teal-400' : 'text-slate-300 dark:text-slate-700'}`}>
                              {tp.praktikum ?? '-'}
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="py-6 flex flex-col items-center justify-center text-center opacity-40">
                        <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Belum Ada TP</p>
                      </div>
                    )}

                    {/* Summary Card for Sumatif Akhir and Nilai Rapor */}
                    <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800 space-y-3">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="p-2.5 bg-slate-50 dark:bg-slate-800/30 rounded-xl border border-slate-100 dark:border-slate-800/50 text-center">
                          <p className="text-[8px] font-black uppercase text-slate-400 tracking-wider">Sumatif Akhir (SAS)</p>
                          <p className="text-xl font-black text-indigo-600 dark:text-indigo-400 mt-1">
                            {computedGradesForSubject.sasScore ?? '-'}
                          </p>
                        </div>
                        <div className="p-2.5 bg-indigo-50/50 dark:bg-indigo-950/20 rounded-xl border border-indigo-100/35 dark:border-indigo-900/30 text-center">
                          <p className="text-[8px] font-black uppercase text-indigo-500 tracking-wider">Nilai Rapor (Akhir)</p>
                          <p className="text-xl font-black text-indigo-700 dark:text-indigo-300 mt-1">
                            {computedGradesForSubject.finalScoreRounded} 
                            <span className="text-[10px] font-bold text-indigo-400 ml-1">
                              ({computedGradesForSubject.finalScore})
                            </span>
                          </p>
                        </div>
                      </div>

                      <div className="p-2.5 bg-slate-50 dark:bg-slate-800/20 rounded-xl border border-slate-100 dark:border-slate-800/50 text-xs text-slate-500 font-bold space-y-1">
                        <div className="flex justify-between">
                          <span>Rata Formatif:</span>
                          <span className="text-slate-700 dark:text-slate-300 font-black">{computedGradesForSubject.avgSumatif}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Rata Praktik:</span>
                          <span className="text-slate-700 dark:text-slate-300 font-black">{computedGradesForSubject.avgPraktikum}</span>
                        </div>
                        <div className="flex justify-between text-[10px] text-slate-400 border-t border-slate-200/40 dark:border-slate-800/40 pt-1 mt-1">
                          <span>Bobot Nilai:</span>
                          <span>S:{computedGradesForSubject.weights.sumatif}% | P:{computedGradesForSubject.weights.praktikum}% | SAS:{computedGradesForSubject.weights.sas}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="py-8 flex flex-col items-center justify-center text-center">
                    <Loader2 className="animate-spin text-indigo-500 mb-2" size={24} />
                    <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">Memuat Data...</p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-black text-slate-800 dark:text-white tracking-tight">Riwayat Akses Profil</h2>
          <p className="text-sm text-slate-500 font-bold">Pantau siapa saja yang mengakses profil siswa</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
            <input
              type="text"
              placeholder="Cari nama siswa / pengakses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full sm:w-80 pl-10 pr-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition-all font-medium"
            />
          </div>
          {onClearAll && logs.length > 0 && (
            <button
              onClick={() => setShowClearAllConfirm(true)}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 dark:bg-red-900/20 dark:hover:bg-red-900/40 dark:text-red-400 rounded-2xl text-sm font-bold transition-colors"
            >
              <Trash2 size={18} />
              Bersihkan Semua
            </button>
          )}
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-[2rem] shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
                <th className="p-4 text-xs font-black text-slate-500 uppercase tracking-wider whitespace-nowrap">Waktu</th>
                <th className="p-4 text-xs font-black text-slate-500 uppercase tracking-wider whitespace-nowrap">Pengakses</th>
                <th className="p-4 text-xs font-black text-slate-500 uppercase tracking-wider whitespace-nowrap">Profil Siswa</th>
                <th className="p-4 text-xs font-black text-slate-500 uppercase tracking-wider whitespace-nowrap">Perangkat & OS</th>
                <th className="p-4 text-xs font-black text-slate-500 uppercase tracking-wider whitespace-nowrap">IP Address</th>
                <th className="p-4 text-xs font-black text-slate-500 uppercase tracking-wider whitespace-nowrap">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {filteredLogs.length > 0 ? (
                filteredLogs.map(log => {
                  const student = log.studentData;
                  return (
                    <tr key={log.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                      <td className="p-4">
                        <div className="flex items-center gap-2 text-sm font-bold text-slate-600 dark:text-slate-300">
                          <Clock size={16} className="text-slate-400" />
                          {new Date(log.timestamp).toLocaleString('id-ID')}
                        </div>
                      </td>
                      <td className="p-4">
                        <p className="text-sm font-bold text-slate-800 dark:text-white">{log.accessorName}</p>
                        <p className="text-[10px] text-indigo-600 font-black uppercase tracking-wider">{log.accessorRole} {log.accessorNis ? `• ${log.accessorNis}` : ''}</p>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-2xl bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0 shadow-sm">
                            <User size={20} />
                          </div>
                          <div>
                            <button 
                              onClick={() => student && setSelectedStudentForGrades(student)}
                              className="text-sm font-black text-slate-800 dark:text-white text-left hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline transition-all"
                            >
                              {student?.name || 'Siswa Tidak Ditemukan'}
                            </button>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">{student?.nis || 'N/A'}</span>
                              {(log.studentClass || student?.class) && (
                                <span className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 rounded text-[9px] font-black uppercase tracking-widest border border-slate-200 dark:border-slate-700">
                                  Kelas {log.studentClass || student?.class}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-300">
                          <Monitor size={16} className="text-slate-400" />
                          <span>{log.device} • {log.os}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-300">
                          <Globe size={16} className="text-slate-400" />
                          {log.ip}
                        </div>
                      </td>
                      <td className="p-4">
                        <button
                          onClick={() => setLogToDelete(log.id)}
                          className="p-2 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-xl transition-colors"
                          title="Hapus Log"
                        >
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className="p-24 text-center">
                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center text-slate-300 mb-4">
                        <Eye size={32} />
                      </div>
                      <p className="text-slate-500 font-bold">Tidak ada riwayat akses yang ditemukan.</p>
                      <p className="text-[10px] uppercase font-black tracking-widest text-slate-400 mt-2">Daftar akan muncul setelah ada aktivitas</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        <div className="p-4 bg-slate-50 dark:bg-slate-800/20 border-t border-slate-100 dark:border-slate-800 flex justify-center">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
            Menampilkan 10 Aktivitas Terbaru
          </p>
        </div>
      </div>
    </div>
  );
});

export default ProfileAccessHistoryView;
